import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import { X, Eye, Printer } from 'lucide-react'
import { imprimirAlbaran, checkBluetoothSupport } from '../utils/printer'
import './AlbaranDialog.css'

export default function AlbaranesClienteDialog({ cliente, onClose }) {
  const [albaranes, setAlbaranes] = useState([])
  const [productos, setProductos] = useState([])
  const [loading, setLoading] = useState(true)
  const [albaranDetalle, setAlbaranDetalle] = useState(null)

  useEffect(() => {
    cargarDatos()
  }, [])

  async function cargarDatos() {
    try {
      const [productosRes, albaranesRes] = await Promise.all([
        supabase.from('products').select('*'),
        supabase
          .from('deliveries')
          .select(`
            id,
            fecha,
            estado,
            delivery_lines(product_id, cantidad_entregada, cantidad_devuelta, precio_unit)
          `)
          .eq('client_id', cliente.id)
          .order('fecha', { ascending: false })
      ])

      setProductos(productosRes.data || [])
      setAlbaranes(albaranesRes.data || [])
    } catch (error) {
      console.error('Error cargando datos:', error)
      alert('Error al cargar albaranes')
    } finally {
      setLoading(false)
    }
  }

  async function verDetalle(albaran) {
    setAlbaranDetalle(albaran)
  }

  async function imprimirAlbaranDetalle(albaran) {
    const bluetoothCheck = checkBluetoothSupport()
    if (!bluetoothCheck.supported) {
      alert(bluetoothCheck.message)
      return
    }

    try {
      const lineasParaImprimir = albaran.delivery_lines.map(l => ({
        product_id: l.product_id,
        cantidad_entregada: l.cantidad_entregada,
        cantidad_devuelta: l.cantidad_devuelta
      }))

      await imprimirAlbaran(cliente, lineasParaImprimir, productos, albaran.fecha)
      alert('Albarán impreso correctamente')
    } catch (error) {
      console.error('Error imprimiendo:', error)
      alert('Error al imprimir: ' + error.message)
    }
  }

  function calcularTotal(albaran) {
    let total = 0
    const descuentoCliente = Number(cliente.descuento_porcentaje || 0) / 100

    albaran.delivery_lines.forEach(linea => {
      const cantidad = Number(linea.cantidad_entregada || 0)
      let precio = Number(linea.precio_unit || 0)
      
      // Aplicar descuento si corresponde (esto es aproximado, idealmente debería venir de delivery_lines)
      const producto = productos.find(p => p.id === linea.product_id)
      if (producto?.descuento_aplicable && descuentoCliente > 0) {
        precio = precio * (1 - descuentoCliente)
      }
      
      total += cantidad * precio
    })

    return total
  }

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh' }}>
        <div className="dialog-header">
          <div>
            <h2>Albaranes - {cliente.alias || cliente.razon_social}</h2>
            <p className="dialog-subtitle">Historial de entregas</p>
          </div>
          <button className="btn-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="dialog-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>
              Cargando albaranes...
            </div>
          ) : albaranes.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>
              No hay albaranes para este cliente
            </div>
          ) : (
            <div className="tabla-wrapper">
              <table className="tabla-lineas">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Productos</th>
                    <th style={{ textAlign: 'right' }}>Total aprox.</th>
                    <th style={{ width: '120px', textAlign: 'center' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {albaranes.map(albaran => (
                    <tr key={albaran.id}>
                      <td style={{ fontFamily: 'monospace' }}>{albaran.fecha}</td>
                      <td style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
                        {albaran.delivery_lines.length} producto(s)
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 500 }}>
                        {calcularTotal(albaran).toFixed(2)}€
                      </td>
                      <td>
                        <div className="acciones-cell">
                          <button
                            onClick={() => verDetalle(albaran)}
                            className="btn-icon"
                            title="Ver detalle"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => imprimirAlbaranDetalle(albaran)}
                            className="btn-icon"
                            title="Reimprimir"
                          >
                            <Printer size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="dialog-footer">
          <button onClick={onClose} className="btn-secondary">Cerrar</button>
        </div>
      </div>

      {/* Modal detalle */}
      {albaranDetalle && (
        <div className="dialog-overlay" onClick={() => setAlbaranDetalle(null)} style={{ zIndex: 1002 }}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="dialog-header">
              <div>
                <h2>Detalle del Albarán</h2>
                <p className="dialog-subtitle">Fecha: {albaranDetalle.fecha}</p>
              </div>
              <button className="btn-close" onClick={() => setAlbaranDetalle(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="dialog-body">
              <div className="tabla-wrapper">
                <table className="tabla-lineas">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th style={{ width: '80px', textAlign: 'center' }}>Entregado</th>
                      <th style={{ width: '80px', textAlign: 'center' }}>Devuelto</th>
                      <th style={{ width: '100px', textAlign: 'right' }}>Precio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {albaranDetalle.delivery_lines.map((linea, index) => {
                      const producto = productos.find(p => p.id === linea.product_id)
                      return (
                        <tr key={index}>
                          <td>{producto?.nombre || 'Producto no encontrado'}</td>
                          <td style={{ textAlign: 'center' }}>{linea.cantidad_entregada}</td>
                          <td style={{ textAlign: 'center' }}>{linea.cantidad_devuelta || 0}</td>
                          <td style={{ textAlign: 'right' }}>{Number(linea.precio_unit).toFixed(2)}€</td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--line)', fontWeight: 'bold' }}>
                      <td colSpan="3" style={{ textAlign: 'right', paddingTop: '0.75rem' }}>TOTAL:</td>
                      <td style={{ textAlign: 'right', paddingTop: '0.75rem', fontSize: '1.125rem', color: 'var(--accent)' }}>
                        {calcularTotal(albaranDetalle).toFixed(2)}€
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="dialog-footer">
              <button onClick={() => setAlbaranDetalle(null)} className="btn-secondary">Cerrar</button>
              <button onClick={() => imprimirAlbaranDetalle(albaranDetalle)} className="btn-primary">
                <Printer size={18} /> Imprimir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}